/**
 * Bars, from a file that has none.
 *
 * A MIDI file is a stream of events with a time signature written on it, not a piece cut
 * into bars: nothing in it says where a bar starts. The bars are inferred here, by laying
 * metres end to end from tick zero until the last note is covered, and hanging each
 * signature, key and tempo on the bar it falls in.
 *
 * A change that lands in the middle of a bar is applied at the start of the next one and
 * said so in a warning, because a bar of 4/4 that turns into 3/4 halfway through is not a
 * bar anything can write, and guessing which half the writer meant would be guessing.
 */

import { measureTicks, plain } from "../document/duration";
import { C_MAJOR, COMMON_TIME } from "../document/types";
import type { KeySignature, Tempo, TimeSignature } from "../document/types";
import type { MidiFile } from "./read";

/** One bar of the piece, with whatever it has to state at its start. */
export interface PlannedBar {
  readonly start: number;
  readonly ticks: number;
  readonly time: TimeSignature | null;
  readonly key: KeySignature | null;
  readonly tempo: Tempo | null;
  /** The key in force through this bar, for spelling the notes in it. */
  readonly fifths: number;
}

export interface BarPlan {
  readonly bars: readonly PlannedBar[];
  readonly warnings: readonly string[];
}

interface Mark {
  readonly tick: number;
}

/** The marks not yet used, oldest first, with those at or before `clock` taken off. */
function dueAt<T extends Mark>(marks: readonly T[], from: number, clock: number): number {
  let index = from;
  while (index < marks.length && (marks[index] as T).tick <= clock) index += 1;
  return index;
}

function timeOf(beats: number, beatType: number): TimeSignature {
  const legal = beatType > 0 && (beatType & (beatType - 1)) === 0;
  return { beats: Math.max(1, beats), beatType: legal ? beatType : 4, symbol: "number" };
}

function tempoOf(microsPerQuarter: number): Tempo {
  const bpm = microsPerQuarter > 0 ? 60000000 / microsPerQuarter : 120;
  return { bpm: Math.round(bpm * 100) / 100, unit: plain("quarter"), text: null };
}

function lateWarning(what: string, bar: number): string {
  return `the ${what} that changes inside bar ${bar} is written at the start of bar ${bar + 1}, since a bar cannot change halfway through`;
}

/**
 * Lay bars end to end until `until` ticks are covered.
 *
 * `snap` is the caller's own scaling: the file's ticks in, the editor's ticks out, rounded
 * to the grid, so that a signature written a tick early still lands on the bar line.
 */
export function planBars(file: MidiFile, until: number, snap: (tick: number) => number): BarPlan {
  const byTick = <T extends Mark>(marks: readonly T[]): readonly T[] =>
    [...marks].sort((first, second) => first.tick - second.tick);
  const times = byTick(file.times.map((mark) => ({ ...mark, tick: snap(mark.tick) })));
  const keys = byTick(file.keys.map((mark) => ({ ...mark, tick: snap(mark.tick) })));
  const tempos = byTick(file.tempos.map((mark) => ({ ...mark, tick: snap(mark.tick) })));

  const bars: PlannedBar[] = [];
  const warnings: string[] = [];
  let metre = COMMON_TIME;
  let key = C_MAJOR;
  let clock = 0;
  let nextTime = 0;
  let nextKey = 0;
  let nextTempo = 0;

  while (bars.length === 0 || clock < until) {
    const number = bars.length + 1;
    const timeTo = dueAt(times, nextTime, clock);
    let time: TimeSignature | null = null;
    for (let index = nextTime; index < timeTo; index += 1) {
      const mark = times[index] as (typeof times)[number];
      if (mark.tick !== clock && clock > 0) warnings.push(lateWarning("metre", number - 1));
      metre = timeOf(mark.beats, mark.beatType);
      time = metre;
    }
    nextTime = timeTo;

    const keyTo = dueAt(keys, nextKey, clock);
    let stated: KeySignature | null = null;
    for (let index = nextKey; index < keyTo; index += 1) {
      const mark = keys[index] as (typeof keys)[number];
      if (mark.tick !== clock && clock > 0) warnings.push(lateWarning("key", number - 1));
      key = { fifths: mark.fifths, mode: mark.minor ? "minor" : "major" };
      stated = key;
    }
    nextKey = keyTo;

    const tempoTo = dueAt(tempos, nextTempo, clock);
    let tempo: Tempo | null = null;
    for (let index = nextTempo; index < tempoTo; index += 1) {
      tempo = tempoOf((tempos[index] as (typeof tempos)[number]).microsPerQuarter);
    }
    nextTempo = tempoTo;

    const ticks = measureTicks(metre.beats, metre.beatType);
    bars.push({
      start: clock,
      ticks,
      time: bars.length === 0 ? metre : time,
      key: bars.length === 0 ? key : stated,
      tempo,
      fifths: key.fifths,
    });
    clock += ticks;
  }

  return { bars, warnings: [...new Set(warnings)] };
}
